import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import i18n from "i18next";
import api, { TOKEN_KEY } from "../lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      if (data?.language && data.language !== i18n.language) {
        i18n.changeLanguage(data.language);
      }
    } catch (e) {
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    if (data.user?.language) i18n.changeLanguage(data.user.language);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    // New flow: register no longer returns a token; email must be verified first.
    return data; // { needs_verification: true, email }
  };

  const verifyEmail = async (email, code) => {
    const { data } = await api.post("/auth/verify-email", { email, code });
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    if (data.user?.language) i18n.changeLanguage(data.user.language);
    return data.user;
  };

  const resendCode = async (email) => {
    await api.post("/auth/resend-code", { email });
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  };

  const setLanguage = async (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem("rj_lang", lang);
    if (user) {
      try {
        await api.put("/auth/language", { language: lang });
        setUser((u) => (u ? { ...u, language: lang } : u));
      } catch (e) { /* ignore */ }
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, verifyEmail, resendCode, logout, setLanguage, refresh: loadMe }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
